import CONFIG from '../config';

const getBessApiBaseUrl = () => {
  let url = process.env.REACT_APP_BASE_URL + '/bess' || `${CONFIG.API_BASE_URL}/bess`;
  url = url.trim().replace(/\/+$/, '');
  return url;
};

export const BASE_URL = getBessApiBaseUrl();

const getAuthToken = () => {
  if (typeof window !== "undefined") {
    return localStorage.getItem("access_token") || "";
  }
  return "";
};

export async function customFetch(url, options = {}) {
  const token = getAuthToken();
  const headers = {
    ...options.headers,
    "Authorization": `Bearer ${token}`,
  };
  let res = await fetch(url, {
    ...options,
    headers,
  });

  if (res.status === 401) {
    const refresh = localStorage.getItem('refresh_token');
    if (refresh) {
      try {
        const refreshRes = await fetch(`${CONFIG.API_BASE_URL}/auth/refresh/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh }),
        });
        if (refreshRes.ok) {
          const data = await refreshRes.json();
          localStorage.setItem('access_token', data.access);
          headers["Authorization"] = `Bearer ${data.access}`;
          res = await fetch(url, {
            ...options,
            headers,
          });
        } else {
          localStorage.clear();
          window.location.href = '/login?expired=true';
          throw new Error("Session Expired");
        }
      } catch (err) {
        localStorage.clear();
        window.location.href = '/login?expired=true';
        throw new Error("Session Expired");
      }
    } else {
      localStorage.clear();
      window.location.href = '/login?expired=true';
      throw new Error("Session Expired");
    }
  }
  return res;
}

export async function fetchContainers() {
  const res = await customFetch(`${BASE_URL}/static/container-batch/`);
  if (!res.ok) throw new Error("Failed to fetch BESS Containers");
  return res.json();
}

export async function fetchPCS() {
  const res = await customFetch(`${BASE_URL}/static/pcs/`);
  if (!res.ok) throw new Error("Failed to fetch PCS Systems");
  return res.json();
}

export async function saveSimulation(data) {
  // If editing an existing simulation draft
  const method = data.id ? "PUT" : "POST";
  const url = data.id
    ? `${BASE_URL}/static/bess-simulation/${data.id}/`
    : `${BASE_URL}/static/bess-simulation/`;

  const cleanData = { ...data };

  const res = await customFetch(url, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(cleanData),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Failed to save BESS simulation draft");
  }
  return res.json();
}

export async function deleteSimulation(id) {
  const res = await customFetch(`${BASE_URL}/static/bess-simulation/${id}/`, {
    method: "DELETE",
  });
  if (!res.ok) {
    throw new Error("Failed to delete BESS simulation");
  }
  return true;
}

export async function freezeSimulation(id) {
  const res = await customFetch(`${BASE_URL}/static/bess-simulation/${id}/`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ is_frozen: true }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || err.detail || "Failed to freeze BESS simulation");
  }
  return res.json();
}

export async function deleteHybridSimulation(id) {
  const res = await customFetch(`${BASE_URL}/static/hybrid-bess-simulation/${id}/`, {
    method: "DELETE",
  });
  if (!res.ok) {
    throw new Error("Failed to delete hybrid BESS simulation");
  }
  return true;
}

export async function runSimulation(id) {
  const res = await customFetch(`${BASE_URL}/simulation/run/${id}/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Failed to run simulation sizing analysis");
  }
  return res.json();
}

export async function fetchSimulationDetails(id) {
  const res = await customFetch(`${BASE_URL}/simulation/run/${id}/`);
  if (!res.ok) throw new Error("Failed to fetch simulation results");
  return res.json();
}

export async function createContainerBatch(data) {
  const res = await customFetch(`${BASE_URL}/static/container-batch/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Failed to save BESS Container");
  }
  return res.json();
}

export async function deleteContainerBatch(id) {
  const res = await customFetch(`${BASE_URL}/static/container-batch/${id}/`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || err.detail || "Failed to delete BESS Container");
  }
  return true;
}

export async function uploadContainerAuxPower(containerId, file) {
  const formData = new FormData();
  formData.append("container_id", containerId);
  formData.append("file", file);

  const res = await customFetch(`${BASE_URL}/container-aux-power/upload/`, {
    method: "POST",
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Failed to upload Container AUX Power profile.");
  }
  return res.json();
}

export async function uploadContainerPerformance(containerId, file) {
  const formData = new FormData();
  formData.append("container_batch_ids", containerId);
  formData.append("file", file);

  const res = await customFetch(`${BASE_URL}/container-performance/upload/`, {
    method: "POST",
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Failed to upload Container Performance profile.");
  }
  return res.json();
}

export async function createPCS(data) {
  const res = await customFetch(`${BASE_URL}/static/pcs/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Failed to save PCS profile");
  }
  return res.json();
}

export async function deletePCS(id) {
  const res = await customFetch(`${BASE_URL}/static/pcs/${id}/`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || err.detail || "Failed to delete PCS profile");
  }
  return true;
}

export async function uploadPcsOutputPower(pcsId, file) {
  const formData = new FormData();
  formData.append("pcs_id", pcsId);
  formData.append("file", file);

  const res = await customFetch(`${BASE_URL}/pcs-output-power/upload/`, {
    method: "POST",
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Failed to upload PCS Output Power profile.");
  }
  return res.json();
}

// ==========================================
// Added API methods for manual entry & retrieval
// ==========================================

export async function fetchContainerPerformances(containerBatchId) {
  const url = containerBatchId
    ? `${BASE_URL}/static/container-performance/?container_batch=${containerBatchId}`
    : `${BASE_URL}/static/container-performance/`;
  const res = await customFetch(url);
  if (!res.ok) throw new Error("Failed to fetch Container Performances");
  return res.json();
}

export async function fetchContainerAuxPowers(containerId) {
  const url = containerId
    ? `${BASE_URL}/static/container-aux-power/?container=${containerId}`
    : `${BASE_URL}/static/container-aux-power/`;
  const res = await customFetch(url);
  if (!res.ok) throw new Error("Failed to fetch Container AUX Powers");
  return res.json();
}

export async function fetchPCSOutputPowers(pcsId) {
  const url = pcsId
    ? `${BASE_URL}/static/pcs-output-power/?pcs=${pcsId}`
    : `${BASE_URL}/static/pcs-output-power/`;
  const res = await customFetch(url);
  if (!res.ok) throw new Error("Failed to fetch PCS Output Powers");
  return res.json();
}

export async function createContainerPerformance(data) {
  const res = await customFetch(`${BASE_URL}/static/container-performance/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Failed to save Container Performance record");
  }
  return res.json();
}

export async function updateContainerPerformance(id, data) {
  const res = await customFetch(`${BASE_URL}/static/container-performance/${id}/`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || err.detail || "Failed to update Container Performance record");
  }
  return res.json();
}

export async function deleteContainerPerformance(id) {
  const res = await customFetch(`${BASE_URL}/static/container-performance/${id}/`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || err.detail || "Failed to delete Container Performance record");
  }
  return true;
}

export async function createContainerAuxPower(data) {
  const res = await customFetch(`${BASE_URL}/static/container-aux-power/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Failed to save Container AUX Power record");
  }
  return res.json();
}

export async function updateContainerAuxPower(id, data) {
  const res = await customFetch(`${BASE_URL}/static/container-aux-power/${id}/`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || err.detail || "Failed to update Container AUX Power record");
  }
  return res.json();
}

export async function deleteContainerAuxPower(id) {
  const res = await customFetch(`${BASE_URL}/static/container-aux-power/${id}/`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || err.detail || "Failed to delete Container AUX Power record");
  }
  return true;
}

export async function createPCSOutputPower(data) {
  const res = await customFetch(`${BASE_URL}/static/pcs-output-power/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Failed to save PCS Output Power record");
  }
  return res.json();
}

export async function updatePCSOutputPower(id, data) {
  const res = await customFetch(`${BASE_URL}/static/pcs-output-power/${id}/`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || err.detail || "Failed to update PCS Output Power record");
  }
  return res.json();
}

export async function deletePCSOutputPower(id) {
  const res = await customFetch(`${BASE_URL}/static/pcs-output-power/${id}/`, {
    method: "DELETE",
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || err.detail || "Failed to delete PCS Output Power record");
  }
  return true;
}

export async function fetchSimulations() {
  const res = await customFetch(`${BASE_URL}/static/bess-simulation/`);
  if (!res.ok) throw new Error("Failed to fetch BESS Simulations");
  return res.json();
}

export async function fetchHybridSimulations(simType = null) {
  let url = `${BASE_URL}/static/hybrid-bess-simulation/`;
  if (simType !== null && simType !== undefined) {
    url += `?simulation_type=${simType}`;
  }
  const res = await customFetch(url);
  if (!res.ok) throw new Error("Failed to fetch Hybrid BESS Simulations");
  return res.json();
}

export async function fetchHybridBessHistory() {
  const res = await customFetch(`${BASE_URL}/hybrid-bess/history/`);
  if (!res.ok) throw new Error("Failed to fetch Hybrid BESS Solar History");
  return res.json();
}

export async function fetchHybridBessWindHistory() {
  const res = await customFetch(`${BASE_URL}/hybrid-bess-wind/history/`);
  if (!res.ok) throw new Error("Failed to fetch Hybrid BESS Wind History");
  return res.json();
}

export async function createHybridSimulation(data, simId = null) {
  const url = simId
    ? `${BASE_URL}/static/hybrid-bess-simulation/${simId}/`
    : `${BASE_URL}/static/hybrid-bess-simulation/`;
  const method = simId ? "PATCH" : "POST";

  const res = await customFetch(url, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const errMsg =
      err.error ||
      err.detail ||
      (typeof err === "object"
        ? Object.entries(err)
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
            .join("; ")
        : null) ||
      "Failed to save Hybrid BESS Simulation";
    throw new Error(errMsg);
  }
  return res.json();
}

export async function fetchHybridDischargeProfile(simId) {
  const res = await customFetch(`${BASE_URL}/hybrid-simulation/${simId}/discharge-profile/`);
  if (!res.ok) throw new Error("Failed to fetch hybrid BESS discharge profile");
  return res.json();
}

export async function saveHybridDischargeProfile(simId, profileData) {
  const res = await customFetch(`${BASE_URL}/hybrid-simulation/${simId}/discharge-profile/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(profileData),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Failed to update discharge profile");
  }
  return res.json();
}

export async function fetchHybridChargingProfile(simId) {
  const res = await customFetch(`${BASE_URL}/hybrid-simulation/${simId}/charging-profile/`);
  if (!res.ok) throw new Error("Failed to fetch hybrid BESS charging profile");
  return res.json();
}

export async function saveHybridChargingProfile(simId, profileData) {
  const res = await customFetch(`${BASE_URL}/hybrid-simulation/${simId}/charging-profile/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(profileData),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Failed to update charging profile");
  }
  return res.json();
}

export async function fetchHybridGridChargingProfile(simId) {
  const res = await customFetch(`${BASE_URL}/hybrid-simulation/${simId}/grid-charging-profile/`);
  if (!res.ok) throw new Error("Failed to fetch hybrid BESS grid charging profile");
  return res.json();
}

export async function saveHybridGridChargingProfile(simId, profileData) {
  const res = await customFetch(`${BASE_URL}/hybrid-simulation/${simId}/grid-charging-profile/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(profileData),
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Failed to update grid charging profile");
  }
  return res.json();
}


export async function uploadSimulationGenerationProfile(simId, solarDegradation, file) {
  const formData = new FormData();
  formData.append("simulation_id", simId);
  if (solarDegradation !== undefined && solarDegradation !== null && solarDegradation !== "") {
    formData.append("solar_degradation", solarDegradation);
  }
  formData.append("file", file);

  const res = await customFetch(`${BASE_URL}/simulation/generation-profile/upload/`, {
    method: "POST",
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Failed to upload simulation generation profile");
  }
  return res.json();
}

export async function runHybridSimulation(simId) {
  const res = await customFetch(`${BASE_URL}/hybrid-simulation/run/${simId}/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error || "Failed to execute hybrid simulation run");
  }
  return res.json();
}

export async function fetchHybridSimulationHourlyProfile(simId, year) {
  const res = await customFetch(`${BASE_URL}/hybrid-simulation/run/${simId}/?year=${year}`);
  if (!res.ok) throw new Error("Failed to fetch hourly block profiles");
  return res.json();
}

export async function fetchSimulationConfig(id) {
  const res = await customFetch(`${BASE_URL}/static/bess-simulation/${id}/`);
  if (!res.ok) throw new Error("Failed to fetch simulation configuration");
  return res.json();
}

export async function fetchHybridSimulationConfig(id) {
  const res = await customFetch(`${BASE_URL}/static/hybrid-bess-simulation/${id}/`);
  if (!res.ok) throw new Error("Failed to fetch hybrid simulation configuration");
  return res.json();
}

export async function downloadHybridReport(simulationId) {
  const res = await customFetch(`${BASE_URL}/hybrid-simulation/run/${simulationId}/?year=all`);
  if (!res.ok) throw new Error("Failed to fetch simulation report data");
  return res.json();
}

export async function downloadHybridDailyReport(simulationId, year = "daily") {
  const res = await customFetch(`${BASE_URL}/hybrid-simulation/run/${simulationId}/?year=${year}`);
  if (!res.ok) throw new Error("Failed to fetch daily simulation report data");
  return res.json();
}

export async function downloadHybridSimulationCSV(simulationId, startYear = 1, numYears = 25) {
  const params = new URLSearchParams();
  if (startYear) params.append("start_year", startYear);
  if (numYears) params.append("number_of_years", numYears);
  const queryString = params.toString();
  const res = await customFetch(`${BASE_URL}/simulation/hybrid/export-csv/${simulationId}/?${queryString}`);
  if (!res.ok) throw new Error("Failed to download CSV");
  return res.blob();
}
